import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import RestoreIcon from '@mui/icons-material/Restore';
import { RecentValuesMenu } from './RecentValuesMenu';

/**
 * "The one from a moment ago" — F2 on a field, and the list behind it.
 *
 * Recording is repetitive: forty photos off one film, a week of Zeichenblätter
 * by the same Zeichner. The suggestion list already holds those values, but it
 * is alphabetical and has to be searched. This offers the same values by
 * RECENCY instead (see ./recentValues), a keystroke away:
 *
 * **F2 opens the list**, newest first and focused, so Enter takes it and the
 * arrows walk back in time. Esc leaves everything as it was.
 *
 * It does NOT fill the field on its own: overwriting what somebody is typing
 * with a value they cannot see yet saves a keypress and costs a value that
 * arrived unasked and may be wrong. Enter is not part of this either — in a
 * free-text field Enter means "take what I typed".
 *
 * `trigger` is a button doing the same thing, for surfaces without a keyboard.
 * It is OPT-IN, and the grid cells deliberately leave it out: a column is
 * 130-190pt wide and an icon takes a quarter of what is left to type in. A form
 * field has the width to spare.
 */
export interface RecentValueEntry {
    /** Put on the input; handles F2. */
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
    /**
     * An opt-in button for surfaces with room to spare — same effect as F2.
     * Null when there is nothing to offer; the grid cells do not render it.
     */
    trigger: ReactNode;
    /** The list; render anywhere, it anchors itself. */
    menu: ReactNode;
}

export function useRecentValueEntry({
    recent = [],
    value,
    onPick
}: {
    /** The last values of this field, newest first (see recentValues). */
    recent?: readonly string[];
    /** What the field holds now — marked in the list, never overwritten by it. */
    value: string;
    onPick: (value: string) => void;
}): RecentValueEntry {
    const intl = useIntl();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    // Where to put the caret back after the list closes: a person picking a
    // value is mid-entry, and the next thing they do is type or tab on.
    const inputRef = useRef<HTMLElement | null>(null);

    const label = intl.formatMessage({ id: 'recent.tooltip' });

    function pick(picked: string) {
        onPick(picked);
        setAnchor(null);
        inputRef.current?.focus();
    }

    function onKeyDown(event: KeyboardEvent<HTMLElement>) {
        if (event.key !== 'F2' || recent.length === 0) {
            return;
        }
        // Swallowed: F2 is the grid's own "start editing", and the cell
        // underneath must not act on the key that opened the list.
        event.preventDefault();
        event.stopPropagation();
        inputRef.current = event.currentTarget;
        setAnchor(event.currentTarget);
    }

    return {
        onKeyDown,
        trigger:
            recent.length === 0 ? null : (
                <Tooltip title={label}>
                    <IconButton
                        size="small"
                        edge="end"
                        aria-label={label}
                        // The field keeps the caret: opening the list must not
                        // read as leaving the input.
                        onMouseDown={(event) => event.preventDefault()}
                        // Out of the Tab order — F2 opens it (see
                        // useFormRecentValues).
                        tabIndex={-1}
                        onClick={(event) => setAnchor(event.currentTarget)}
                    >
                        <RestoreIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        menu: (
            <RecentValuesMenu
                anchor={anchor}
                values={recent}
                current={value}
                onPick={pick}
                onClose={() => setAnchor(null)}
            />
        )
    };
}
